import * as UI from "@iyk/ui"
import { Card } from "@iyk/vf-ui"
import * as React from "react"
import { useLocation } from "react-router"
import { appConfig } from "../../app-config.ts"
import { VerifiedProfileIcon } from "./icons.tsx"
import { Sheet } from "./sheet.tsx"

export const CollectionShare = (props: CollectionShare.Props) => {
  const pathname = useLocation().pathname
  const [isCopied, setIsCopied] = React.useState(false)

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(getShareUrl(pathname))
      setIsCopied(true)
      setTimeout(() => setIsCopied(false), 2000)
    } catch (error) {
      console.error("Failed to copy:", error)
    }
  }

  return (
    <Sheet.Root>
      <Sheet.Trigger asChild>
        <UI.Button type="button" kind="ghost" size="sm" className="absolute top-4 right-4">
          <UI.Icon.Upload />
        </UI.Button>
      </Sheet.Trigger>
      <Sheet.Content
        title="Share your collection"
        description={`You've earned it - now show off your verified fandom and let the world know you're a fan of ${appConfig.artist.name}`}
      >
        <div className="flex flex-col gap-4 px-4 py-6">
          <h3 className="text-base-title">Share your collection</h3>
          <p className="text-sm tex-gray-11">
            You've earned it - now show off your verified fandom and let the world know you're a fan of{" "}
            {appConfig.artist.name}
          </p>
          <Card className="flex justify-between p-4">
            <div className="flex items-center gap-2">
              <span className="text-[18px] leading-6 uppercase font-departure-mono">{props.username}</span>
              <VerifiedProfileIcon />
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <UI.Icon.Location />
                <span className="text-xs">{props.totalCheckIns}</span>
              </div>
              <div className="flex items-center gap-2">
                <UI.Icon.Badge />
                <span className="text-xs">{props.totalMerchOwned}</span>
              </div>
            </div>
          </Card>
          <UI.Button type="button" kind="solid" size="md" className="w-full" onClick={copyToClipboard}>
            {isCopied ? <UI.Icon.Check /> : <UI.Icon.Copy />}
            Copy link
          </UI.Button>
          <div className="flex items-center justify-center gap-1">
            {ShareLinks.map((link, index) => (
              <a
                key={index}
                className={UI.classForButton({ kind: "ghost", size: "md" })}
                href={link.url(getShareUrl(pathname))}
                target="_blank"
                rel="noreferrer"
              >
                {link.icon}
              </a>
            ))}
          </div>
        </div>
      </Sheet.Content>
    </Sheet.Root>
  )
}

const SHARE_COPY = `Just got verified as an official ${appConfig.artist.name} fan. Check out all my proofs below!`

function getShareUrl(pathname: string) {
  const origin = typeof window !== "undefined" ? window.location.origin : ""
  return `${origin}${pathname}`
}

const ShareLinks = [
  {
    icon: <UI.Icon.X />,
    url: (shareUrl: string) => twitterLink(shareUrl, SHARE_COPY)
  },
  {
    icon: <UI.Icon.Facebook />,
    url: (shareUrl: string) => facebookLink(shareUrl, SHARE_COPY)
  },
  {
    icon: <UI.Icon.Whatsapp />,
    url: (shareUrl: string) => whatsappLink(shareUrl, SHARE_COPY)
  }
]

const isMobile = () => /android|iphone|ipad|mobile/i.test(navigator.userAgent)

// If the user is on mobile, we can write a post through the custom URL scheme.
// If the user does not have the app installed, then the browser will say that
// there is an error when trying to open the link. This is an edge case as we
// assume that most users have the app installed
// If the user is on desktop, we will use the web app
const facebookLink = (url: string, text: string) => {
  if (isMobile()) {
    return `fb://composer?text=${encodeURIComponent(text + " " + url)}`
  }
  return "https://www.facebook.com/sharer/sharer.php?" + new URLSearchParams({ u: url })
}

// If the user is on mobile, we can send text to whatsapp through the api
// Otherwise, we need to use the web app
const whatsappLink = (url: string, text: string) =>
  `https://${isMobile() ? "api" : "web"}.whatsapp.com/send?` + new URLSearchParams({ text: `${text} ${url}` })

const twitterLink = (url: string, text: string) =>
  "https://twitter.com/intent/tweet?" + new URLSearchParams({ url, text })

namespace CollectionShare {
  export type Props = {
    username: string
    totalCheckIns: number
    totalMerchOwned: number
  }
}
